import { FormikErrors, FormikTouched } from "formik";
import { useMemo } from "react";
import ColorPicker from "rsuite-color-picker";
import { THeatmapLegendItem } from "../../../../api/types/Heatmap";
import svgIcons from "../../../../assets/svg/svgIcons";
import FormActionButtons, {
  FormActionButton,
} from "../../../../components/admin/formActionButtons";
import { AdminFormFieldInput } from "../../../../components/admin/formFields";
import { RsuitePickedColor } from "./types";
import { useTranslation } from "react-i18next";

type HeatmapLegendConfigFieldProps = {
  errors: string | FormikErrors<THeatmapLegendItem> | undefined;
  handleBlur: (event: React.FocusEvent<any>) => void;
  handleAddLegend: () => void;
  handleColorChange: (name: string, value: RsuitePickedColor) => void;
  handleInputFieldChange: (event: React.ChangeEvent<any>) => void;
  handleRemoveLegend?: () => void;
  index: number;
  showRemoveButton: boolean;
  touched: FormikTouched<THeatmapLegendItem> | undefined;
  userCanEdit: boolean;
  value: THeatmapLegendItem;
};

const HeatmapLegendConfigField = ({
  errors,
  handleBlur,
  handleAddLegend,
  handleColorChange,
  handleInputFieldChange,
  handleRemoveLegend,
  index,
  showRemoveButton,
  touched,
  userCanEdit,
  value,
}: HeatmapLegendConfigFieldProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.heatmap.detailsPage",
  });
  const { t: tMain } = useTranslation();

  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              buttonDimensionClass: "h-11 w-full px-4",
              customWrapperClass: "col-span-2 mt-6",
              type: "button",
              variant: "primary",
              label: (
                <span className="flex gap-2">
                  <img
                    className="transition-all group-disabled:opacity-50 group-enabled:group-focus:grayscale-0 group-enabled:group-focus:brightness-0 group-enabled:group-focus:invert group-enabled:group-hover:grayscale-0 group-enabled:group-hover:brightness-0 group-enabled:group-hover:invert"
                    src={svgIcons.ButtonIconPlus}
                    alt=""
                  />
                  {tMain("add")}
                </span>
              ),
              onClick: handleAddLegend,
              // disabled: !userCanEdit,
            },
            ...(showRemoveButton
              ? [
                  {
                    buttonDimensionClass: "h-11 w-full px-4",
                    customWrapperClass: "col-span-1 mt-6 max-w-12",
                    type: "button",
                    variant: "danger",
                    label: (
                      <img
                        // className="transition-all group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"
                        src={svgIcons.CrossRedIcon}
                        alt=""
                      />
                    ),
                    onClick: handleRemoveLegend,
                  } as FormActionButton,
                ]
              : []),
          ]}
        />
      </>
    );
  }, [showRemoveButton]); // userCanEdit, handleRemoveLegend
  return (
    <div className="col-span-12 grid grid-cols-12 gap-4">
      <div className="col-span-2 flex flex-col">
        <label className="block text-sm font-display font-semibold text-field-label-valid mb-1">
          {t("color")}
        </label>
        <div className="flex h-11">
          <ColorPicker
            value={value.color || "#00bcd4"}
            onChange={(value: RsuitePickedColor) => {
              handleColorChange(`legends[${index}][color]`, value);
            }}
            disabled={!userCanEdit}
          />
        </div>
      </div>
      <AdminFormFieldInput
        basicInputClass="w-full h-11 rounded-md text-sm pe-20"
        customWrapperClass="col-span-3"
        innerWrapperClass="mt-1 relative flex w-full after:content-['hours'] after:absolute after:opacity-50 after:top-1/2 after:right-4 after:-translate-y-1/2"
        label={t("from")}
        type="number"
        id={`from_${index}`}
        name={`legends[${index}][start_hour]`}
        value={value.start_hour}
        onChange={handleInputFieldChange}
        onBlur={handleBlur}
        touched={touched?.start_hour}
        error={
          !!errors
            ? (errors as FormikErrors<THeatmapLegendItem>).start_hour
            : undefined
        }
        disabled={!userCanEdit}
        detailsFormField={true}
      />
      <AdminFormFieldInput
        basicInputClass="w-full h-11 rounded-md text-sm pe-20"
        customWrapperClass="col-span-3"
        innerWrapperClass="mt-1 relative flex w-full after:content-['hours'] after:absolute after:opacity-50 after:top-1/2 after:right-4 after:-translate-y-1/2"
        label={t("to")}
        type="number"
        id={`to_${index}`}
        name={`legends[${index}][end_hour]`}
        value={value.end_hour}
        onChange={handleInputFieldChange}
        onBlur={handleBlur}
        touched={touched?.end_hour}
        error={
          !!errors
            ? (errors as FormikErrors<THeatmapLegendItem>).end_hour
            : undefined
        }
        disabled={!userCanEdit}
        detailsFormField={true}
      />
      {/* {MemoizedFormActionButtons} */}
    </div>
  );
};
export default HeatmapLegendConfigField;
